import { useState, FormEvent } from "react";

interface QuoteFormProps {
  light?: boolean;
}

export default function QuoteForm({ light }: QuoteFormProps) {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((val, key) => data.append(key, val.toString()));
    try {
      await fetch("https://readdy.ai/api/form/d75tnorq7u9ee9hce5f0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  const sectionBg = light ? "bg-gray-50" : "bg-[#071220]";
  const labelColor = light ? "text-[#0EA5E9]" : "text-[#7DD3FC]";
  const headingColor = light ? "text-gray-900" : "text-white";
  const bodyColor = light ? "text-gray-500" : "text-white/55";
  const iconBoxBg = light ? "bg-[#0EA5E9]/10 border-[#0EA5E9]/20" : "bg-[#0EA5E9]/10 border-[#0EA5E9]/20";
  const contactLabel = light ? "text-gray-400" : "text-white/40";
  const contactValue = light ? "text-gray-800 hover:text-[#0EA5E9]" : "text-white hover:text-[#7DD3FC]";
  const formBg = light ? "bg-white border-gray-200" : "bg-[#050F1A] border-white/8";
  const inputBg = light ? "bg-gray-50 border-gray-200 focus:border-[#0EA5E9]/60 text-gray-900 placeholder:text-gray-400" : "bg-[#030C15] border-white/10 focus:border-[#0EA5E9]/50 text-white placeholder:text-white/25";
  const inputLabel = light ? "text-gray-500" : "text-white/55";

  return (
    <section id="quote" className={`${sectionBg} py-28`}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left — Info */}
          <div>
            <p className={`font-['Inter'] ${labelColor} text-xs font-semibold uppercase tracking-[0.18em] mb-4`}>
              Get Started
            </p>
            <h2 className={`font-['Playfair_Display'] ${headingColor} text-4xl md:text-5xl font-bold mb-6 leading-tight`}>
              Request Your Free, No-Obligation Quote
            </h2>
            <p className={`font-['Inter'] ${bodyColor} text-base leading-[1.85] mb-10`}>
              Fill out the form and a Knight In Shining Armor HVAC specialist will contact you promptly to discuss your needs and provide accurate, transparent pricing.
            </p>

            {/* Contact Info */}
            <div className="space-y-6">
              {[
                { icon: "ri-phone-fill", label: "Call Us Directly", value: "214-870-3185", href: "tel:2148703185" },
                { icon: "ri-mail-line", label: "Email Us", value: "pruitt@kisacoolingdallas.com", href: "mailto:pruitt@kisacoolingdallas.com" },
                { icon: "ri-map-pin-2-line", label: "Our Location", value: "833 Inwood Dr, McKinney, TX 75069", href: "#" },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-4">
                  <div className={`w-11 h-11 flex items-center justify-center rounded-xl border flex-shrink-0 ${iconBoxBg}`}>
                    <i className={`${item.icon} text-[#0EA5E9] text-lg`}></i>
                  </div>
                  <div>
                    <div className={`font-['Inter'] ${contactLabel} text-xs mb-0.5`}>{item.label}</div>
                    <a
                      href={item.href}
                      className={`font-['Inter'] ${contactValue} text-sm font-medium transition-colors duration-300`}
                    >
                      {item.value}
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Form */}
          <div className={`border rounded-2xl p-8 ${formBg}`}>
            {submitted ? (
              <div className="flex flex-col items-center justify-center text-center py-12">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#0EA5E9]/15 border border-[#0EA5E9]/35 mb-5">
                  <i className="ri-check-line text-[#0EA5E9] text-3xl"></i>
                </div>
                <h3 className={`font-['Playfair_Display'] ${headingColor} text-2xl font-bold mb-3`}>
                  Request Received!
                </h3>
                <p className={`font-['Inter'] ${bodyColor} text-sm leading-relaxed max-w-xs`}>
                  A KISA specialist will contact you shortly to discuss your cooling needs and provide accurate pricing.
                </p>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className={`font-['Inter'] ${inputLabel} text-xs uppercase tracking-wide mb-2 block`}>Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="John Smith"
                      className={`w-full border text-sm font-['Inter'] px-4 py-3 rounded-lg outline-none transition-colors duration-300 ${inputBg}`}
                    />
                  </div>
                  <div>
                    <label className={`font-['Inter'] ${inputLabel} text-xs uppercase tracking-wide mb-2 block`}>Phone *</label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      placeholder="(214) 000-0000"
                      className={`w-full border text-sm font-['Inter'] px-4 py-3 rounded-lg outline-none transition-colors duration-300 ${inputBg}`}
                    />
                  </div>
                </div>
                <div>
                  <label className={`font-['Inter'] ${inputLabel} text-xs uppercase tracking-wide mb-2 block`}>Email</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="john@email.com"
                    className={`w-full border text-sm font-['Inter'] px-4 py-3 rounded-lg outline-none transition-colors duration-300 ${inputBg}`}
                  />
                </div>
                <div>
                  <label className={`font-['Inter'] ${inputLabel} text-xs uppercase tracking-wide mb-2 block`}>Service Needed *</label>
                  <select
                    name="service"
                    required
                    className={`w-full border text-sm font-['Inter'] px-4 py-3 rounded-lg outline-none transition-colors duration-300 cursor-pointer ${inputBg}`}
                  >
                    <option value="">Select a service...</option>
                    <option value="AC Repair">AC Repair</option>
                    <option value="AC Installation & Replacement">AC Installation &amp; Replacement</option>
                    <option value="Preventative Maintenance">Preventative Maintenance</option>
                    <option value="Emergency HVAC Service">Emergency HVAC Service</option>
                    <option value="Air Duct Cleaning">Air Duct Cleaning</option>
                    <option value="Furnace Repair & Heating">Furnace Repair &amp; Heating</option>
                    <option value="System Diagnostics">System Diagnostics &amp; Optimization</option>
                  </select>
                </div>
                <div>
                  <label className={`font-['Inter'] ${inputLabel} text-xs uppercase tracking-wide mb-2 block`}>Message</label>
                  <textarea
                    name="message"
                    rows={4}
                    maxLength={500}
                    placeholder="Describe your cooling issue or project..."
                    className={`w-full border text-sm font-['Inter'] px-4 py-3 rounded-lg outline-none transition-colors duration-300 resize-none ${inputBg}`}
                  ></textarea>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#0EA5E9] hover:bg-[#0284C7] disabled:opacity-60 text-white font-['Inter'] font-bold text-sm py-4 rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer shadow-[0_4px_20px_rgba(14,165,233,0.35)]"
                >
                  {loading ? "Submitting..." : "Get My Free Quote"}
                </button>
                <p className={`font-['Inter'] ${light ? "text-gray-400" : "text-white/30"} text-xs text-center`}>
                  No obligation. We respond within 1 business hour.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
